import type { ESACacheMessage } from '../cache/MessageCache';

const TASK_RESPONSE_TYPES = new Set([
  'task_progress',
  'task_completed',
  'task_failed',
  'task_dispatched',
]);

/**
 * Extracts a human-readable body for one cache message.
 * Prefers plain text / task descriptions; falls back to pretty-printed JSON.
 */
export function messageBodyForTranscript(message: ESACacheMessage): string {
  const payload = message.message ?? {};
  const text = typeof payload.text === 'string' ? payload.text.trim() : '';
  const responseType =
    typeof payload.response_type === 'string' ? payload.response_type : undefined;
  const description =
    typeof payload.description === 'string' ? payload.description.trim() : '';

  if (responseType && TASK_RESPONSE_TYPES.has(responseType) && description) {
    return description;
  }
  if (text) {
    return text;
  }
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
}

export interface FormatConversationTranscriptOptions {
  title: string | null;
  conversationId?: string | null;
  messages: ESACacheMessage[];
}

/** Builds a readable .txt transcript for the active ESA conversation. */
export function formatConversationTranscript(
  options: FormatConversationTranscriptOptions,
): string {
  const title = (options.title ?? '').trim() || 'New conversation';
  const lines: string[] = [
    `Conversation: ${title}`,
  ];
  if (options.conversationId) {
    lines.push(`Conversation ID: ${options.conversationId}`);
  }
  lines.push(`Exported: ${new Date().toISOString()}`, '', '---', '');

  for (const message of options.messages) {
    if (message.id.startsWith('__streaming_')) continue;
    const role = message.initiator === 'user' ? 'User' : 'ESA';
    const stamp = message.createdAt || '';
    const body = messageBodyForTranscript(message);
    lines.push(`[${stamp}] ${role}:`);
    lines.push(body);
    lines.push('');
  }

  return lines.join('\n').trimEnd() + '\n';
}

/** Sanitizes a conversation title into a safe download filename. */
export function buildTranscriptFilename(title: string | null | undefined): string {
  const slug = (title ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return slug ? `${slug}-transcript.txt` : 'conversation-transcript.txt';
}

/** Triggers a browser download of a UTF-8 text file. */
export function downloadTextFile(fileName: string, content: string): void {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}
